import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { FilterOutlined, SearchOutlined } from "@ant-design/icons";
import { isEqual } from "lodash";

import { Input } from "components/form_components/Inputs";
import { ExportOrganisationsButton } from "components/organisations/ExportOrganisationsButton";
import Button from "components/ui/button/Button";
import { ClearSelectedButton } from "lib/core_components/ClearSelectedButton";
import EditColumnsButton from "lib/core_components/EditColumnsButton";
import { createUseDebounce } from "lib/debounce";
import { SearchOrganisationsViaOsQuery } from "lib/generated/app-service-gql/graphql";
import { useOrganisations } from "lib/hooks/api/organisations/useOrganisations";
import { useTablePageSize } from "lib/hooks/useTablePageSize";
import {
  parsePaginationUrlState,
  useResetPageWhenPastEnd,
  useURLState,
} from "lib/hooks/useURLState";
import { SortState } from "lib/search/types";
import { EventDataTypes } from "lib/tracking";
import { ExportOrgCountField } from "lib/types/graphQLEnums";
import { ColumnSetting } from "lib/types/models";
import { useFormAutoSubmit } from "../../lib/hooks/useFormAutoSubmit";
import FilterDrawer from "../filter_form/filter_drawer/FilterDrawer";
import { BUYER_COLUMNS, BuyerColumns } from "./utils/getBuyersColumnCopy";
import { BuyerTableFilters } from "./utils/types";
import { BuyerFilterForm, BuyerFilterSections } from "./BuyerFilterForm";
import { BuyerTable } from "./BuyerTable";
import { QuickFilterBar } from "./QuickFilterBar";
import { SaveBuyerButton } from "./SaveBuyerButton";
import { TableData } from "./types";
import {
  convertBuyerFiltersToOrgFilters,
  DEFAULT_BUYER_FILTERS,
  useFilterableBuyerTableState,
} from "./useFilterableBuyerTableState";

import css from "./FilterableBuyerTable.module.scss";

export function FilterableBuyerTable({
  defaultFilters = DEFAULT_BUYER_FILTERS,
  defaultSortOrder = { field: "noticeCount", order: "DESC" },
  defaultColumns = BUYER_COLUMNS,
  allColumns = BUYER_COLUMNS,
  hiddenSections,
  isSupplierRelationship,
  extraBulkActions,
  hideBulkSave = false,
}: {
  defaultFilters?: BuyerTableFilters;
  defaultSortOrder?: SortState;
  hiddenSections?: BuyerFilterSections[];
  defaultColumns?: ColumnSetting<BuyerColumns>[];
  allColumns?: ColumnSetting<BuyerColumns>[];
  /** Render the buyer table in supplier↔buyer relationship mode. Only valid under a `/suppliers/$id` route; BuyerTable reads the supplier id from the URL. */
  isSupplierRelationship?: boolean;
  /**
   * Extra bulk-action buttons rendered when rows are selected.
   * Receives the selected buyer IDs and a `clearSelection` callback
   * that the action can call after a successful mutation to reset the table state.
   */
  extraBulkActions?: (selectedIds: string[], clearSelection: () => void) => React.ReactNode;
  /** Hide the built-in "Save to list" bulk action (e.g. when already inside a buyer list). */
  hideBulkSave?: boolean;
}) {
  const tableState = useFilterableBuyerTableState(defaultFilters, defaultSortOrder);
  const { buyerTableFilters, setBuyerTableFilters, buyerSort, setBuyerSort } = tableState;
  // Page and page size live in the URL, so Back and shared links show the same rows. Keyed per
  // table because profile tabs carry the URL's search params from one tab to the next.
  const [storedPageSize, setStoredPageSize] = useTablePageSize("buyer-table");
  const [pagination = { page: 1, pageSize: storedPageSize }, setPagination] = useURLState(
    "buyersPagination",
    { page: 1, pageSize: storedPageSize },
    (state) => parsePaginationUrlState(state, { page: 1, pageSize: storedPageSize }),
  );
  const currentPage = pagination.page ?? 1;
  const pageSize = pagination.pageSize ?? storedPageSize;
  const setCurrentPage = useCallback(
    (page: number) => setPagination((prev) => ({ ...prev, page })),
    [setPagination],
  );
  const setPageSize = useCallback(
    (size: number) => {
      setStoredPageSize(size);
      setPagination((prev) => ({ ...prev, pageSize: size }));
    },
    [setStoredPageSize, setPagination],
  );

  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  const [selectedColumns, setSelectedColumns] = useState([...defaultColumns]);
  const [selectedBuyers, setSelectedBuyers] = useState<string[]>([]);

  const useDebounce500 = createUseDebounce(500);
  const debouncedSetBuyerTableFilters = useDebounce500((filters) => {
    updateFilters(filters);
  });

  const values = buyerTableFilters;

  const { control, watch, getValues, trigger } = useForm({
    defaultValues: {
      buyerName: buyerTableFilters.buyerName,
    },
    mode: "onChange",
    values,
  });

  const updateSortState = useCallback(
    (newSortState: SortState) => {
      setBuyerSort(newSortState);
      // Always reset pagination when sort changes
      setCurrentPage(1);
    },
    [setBuyerSort, setCurrentPage],
  );

  const updateFilters = (filters: BuyerTableFilters) => {
    const filtersWithoutUndefined = { ...filters };

    Object.keys(filtersWithoutUndefined).forEach((key) => {
      if (filtersWithoutUndefined[key as keyof BuyerTableFilters] === undefined) {
        delete filtersWithoutUndefined[key as keyof BuyerTableFilters];
      }
    });

    const filtersHaveChangedBackToDefault = isEqual(filtersWithoutUndefined, DEFAULT_BUYER_FILTERS);
    const filtersHaveChanged =
      !isEqual(filters, defaultFilters) && buyerSort.field === defaultSortOrder.field;

    if (filtersHaveChangedBackToDefault || filtersHaveChanged) {
      setBuyerTableFilters(filters);
      setBuyerSort(defaultSortOrder);
    } else {
      setBuyerTableFilters(filters);
    }
    setCurrentPage(1);
  };

  const clearAllFiltersHide = (
    currentFilters: BuyerTableFilters,
    defaultFilters: BuyerTableFilters,
  ) => {
    // Are all filters types selected the same as the default filters?
    if (!isEqual(Object.keys(currentFilters), Object.keys(defaultFilters))) {
      return false;
    }
    // buyersList can be a default filter
    if (
      defaultFilters.buyerLists &&
      !isEqual(currentFilters.buyerLists, defaultFilters.buyerLists)
    ) {
      return false;
    }
    return true;
  };

  useFormAutoSubmit({
    watch,
    getValues,
    trigger,
    onChange: (data) => {
      debouncedSetBuyerTableFilters({ ...buyerTableFilters, buyerName: data.buyerName });
    },
  });

  const filters = useMemo(
    () => convertBuyerFiltersToOrgFilters(buyerTableFilters, buyerSort, pageSize, currentPage),
    [buyerTableFilters, buyerSort, pageSize, currentPage],
  );
  const queryResult = useOrganisations({
    ...filters,
    include: { ...filters.include, buyerLists: true },
  });

  useResetPageWhenPastEnd(
    currentPage,
    pageSize,
    queryResult.data?.searchOrganisations.total,
    setCurrentPage,
  );

  const buyerData: TableData<SearchOrganisationsViaOsQuery | undefined> = {
    queryResult,
    totalCount: queryResult.data?.searchOrganisations.total,
  };

  return (
    <>
      <div className={css.filterBar}>
        <div className={css.filterItems}>
          <form
            className={css.searchInput}
            onSubmit={(e) => {
              e.preventDefault();
              return false;
            }}
          >
            <Input
              placeholder="Search"
              prefix={<SearchOutlined />}
              name="buyerName"
              label=""
              control={control}
              allowClear
            />
          </form>
          <Button buttonType="secondary" onClick={() => setIsFilterDrawerOpen(true)}>
            <FilterOutlined className={css.filterIcon} /> Filters
          </Button>
        </div>
        <div className={css.columnsAndExport}>
          <div className={css.clearSelected}>
            {selectedBuyers.length > 0 && (
              <ClearSelectedButton
                count={selectedBuyers.length}
                onClear={() => setSelectedBuyers([])}
              />
            )}
          </div>
          {selectedBuyers && selectedBuyers.length === 0 && (
            <EditColumnsButton
              allAvailableColumns={allColumns}
              selectedColumns={selectedColumns}
              onNewColumnSettingChange={(c) =>
                setSelectedColumns([...(c as ColumnSetting<BuyerColumns>[])])
              }
              dataType={EventDataTypes.buyer}
            />
          )}
          <div className={css.saveBuyers}>
            {extraBulkActions?.(selectedBuyers, () => setSelectedBuyers([]))}
            {selectedBuyers.length > 0 && !hideBulkSave && (
              <SaveBuyerButton
                buyerGuids={selectedBuyers}
                trackingData={{ "Context source": "Bulk action" }}
              />
            )}
          </div>
          <ExportOrganisationsButton
            filters={filters}
            // BuyerTable renders a single count column — award count in supplier-relationship mode,
            // notice count otherwise — so the export ships the same one.
            countFields={[
              isSupplierRelationship
                ? ExportOrgCountField.AwardCount
                : ExportOrgCountField.NoticeCount,
            ]}
            selectedOrganisationIds={selectedBuyers}
          />
        </div>
      </div>
      <div className={css.viewFilters}>
        <QuickFilterBar
          filters={buyerTableFilters}
          sortState={buyerSort}
          tableData={buyerData}
          onClearFilter={(key) => updateFilters({ ...buyerTableFilters, [key]: undefined })}
          onFilterClick={() => setIsFilterDrawerOpen(true)}
          hiddenSections={hiddenSections || []}
          clearAllFilters={() => updateFilters(defaultFilters)}
          clearAllFiltersHide={() => clearAllFiltersHide(buyerTableFilters, defaultFilters)}
        />
      </div>
      <BuyerTable
        filters={buyerTableFilters}
        sortOrder={buyerSort}
        tableData={buyerData}
        onSortChange={updateSortState}
        selectedRows={selectedBuyers}
        onSelectedRowsChange={(rows) => setSelectedBuyers(rows)}
        selectedColumns={selectedColumns.map((column) => column.field)}
        pagination={{ current: currentPage, pageSize }}
        onPaginationChange={({ current, pageSize }) => {
          setCurrentPage(current);
          setPageSize(pageSize);
        }}
        isSupplierRelationship={isSupplierRelationship}
      />
      <FilterDrawer open={isFilterDrawerOpen} onClose={() => setIsFilterDrawerOpen(false)}>
        {isFilterDrawerOpen && (
          <BuyerFilterForm
            filters={buyerTableFilters}
            onFiltersChange={debouncedSetBuyerTableFilters}
            onClose={() => setIsFilterDrawerOpen(false)}
            options={{
              disabledSections: hiddenSections,
            }}
          />
        )}
      </FilterDrawer>
    </>
  );
}
